import { useState } from 'react'
import { FiX, FiLock, FiAlertTriangle } from 'react-icons/fi'
import axios from 'axios'

/*
 * DesactivarQuioscoModal — se abre tras long-press de 5s en el logo del
 * quiosco. Pide email+password de un admin (de la misma empresa o super_admin)
 * antes de limpiar el flag `kiosk_mode` y volver al login normal.
 *
 * No usa AuthContext.login porque eso establecería sesión y nos llevaría a
 * /v2; aquí solo queremos validar credenciales y, si OK, romper el modo
 * quiosco. Validamos contra /api/auth/login y, si responde 200, comprobamos
 * que la empresa coincide y el rol es admin/super_admin.
 */
export default function DesactivarQuioscoModal({ onClose }) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [cargando, setCargando] = useState(false)
  const [error, setError] = useState(null)

  const empresaIdQuiosco = localStorage.getItem('kiosk_empresa_id')

  const onSubmit = async (e) => {
    e.preventDefault()
    setError(null)
    setCargando(true)
    try {
      const { data } = await axios.post('/api/auth/login', { email, password })
      const rol = data?.empleado?.rol
      const empresaId = data?.empresa?.id

      const esAdminDeEstaEmpresa = rol === 'admin' && String(empresaId) === String(empresaIdQuiosco)
      const esSuperAdmin = rol === 'super_admin'

      if (!esAdminDeEstaEmpresa && !esSuperAdmin) {
        setError('Solo un administrador de esta empresa puede desactivar el modo quiosco.')
        setCargando(false)
        return
      }

      // Cerramos la sesión que acabamos de abrir (no queremos dejar token tras
      // desactivar; el admin volverá a entrar por login normal).
      const token = data.token
      try {
        await axios.post('/api/auth/logout', null, { headers: { Authorization: `Bearer ${token}` } })
      } catch {
        /* best-effort */
      }

      // Limpiar flags del quiosco y token residual
      localStorage.removeItem('kiosk_mode')
      localStorage.removeItem('kiosk_empresa_id')
      localStorage.removeItem('kiosk_empresa_nombre')
      localStorage.removeItem('kiosk_centro_id')
      localStorage.removeItem('kiosk_centro_nombre')
      localStorage.removeItem('token')

      // Recarga dura: garantiza que App.jsx vuelve a evaluar KioskGuard sin
      // el flag y que cualquier estado en memoria queda descartado.
      window.location.replace('/login')
    } catch (err) {
      const msg = err.response?.data?.error || 'Credenciales inválidas'
      setError(msg)
    } finally {
      setCargando(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-orange-50 to-red-50">
          <div className="flex items-center gap-3">
            <FiAlertTriangle className="w-6 h-6 text-orange-600" />
            <h2 className="text-lg font-bold text-gray-900">Desactivar modo quiosco</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Cerrar">
            <FiX className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={onSubmit} className="p-6 space-y-4">
          <p className="text-sm text-gray-600">
            Introduce las credenciales de un administrador para volver a la app normal.
          </p>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="admin@tuempresa.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Contraseña</label>
            <div className="relative">
              <FiLock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={cargando}
              className="flex-1 py-2.5 px-4 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 font-medium disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={cargando}
              className="flex-1 py-2.5 px-4 bg-red-600 hover:bg-red-700 text-white rounded-xl font-medium disabled:opacity-50"
            >
              {cargando ? 'Verificando…' : 'Desactivar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
